import { Card, upNextFields } from '@lingoping/core';
import { StorageManager } from '../storage/index.js';
import { getCloudDecksApi } from './cloudDecks.js';

/**
 * The building blocks of lazy card preparation (Docs/plans/CLOUD_DECKS_PLAN.md): a word the learner
 * chose whose card text cannot be made right now becomes a *pending card* at once. It sits in
 * "Up next to learn" as "Preparing…" and is filled in later (pendingCards.ts). The card itself
 * syncs like any other; what the retry needs but the card has no place for (the context sentence
 * and file translation the word came with, the attempts so far) is kept on this device.
 */

/** A card that has been chosen but whose text has not been prepared yet. */
export function isPendingCard(card: Pick<Card, 'content_status'>): boolean {
  return card.content_status === 'pending';
}

export interface PendingMeta {
  /** The sentence the word was found in, passed to the generator when the card is made. */
  context?: string;
  /** A translation from an imported file, passed to the generator as a hint only. */
  hint?: string;
  attempts: number;
  /** ISO time of the last try; a daily-limit failure is not retried until the next UTC day. */
  lastAttemptAt?: string;
  /** Why the last try failed. */
  code?: string;
}

type MetaMap = Record<string, PendingMeta>;

const META_KEY = 'lingoping_pending_cards_v1';
const COULDNT_ADD_KEY = 'lingoping_couldnt_add_v1';

async function userKey(prefix: string): Promise<string> {
  return `${prefix}_${(await StorageManager.getCurrentUserId()) || 'local-user-1'}`;
}

export async function readPendingMeta(): Promise<MetaMap> {
  return (await StorageManager.getLocalValue<MetaMap>(await userKey(META_KEY))) ?? {};
}

async function writePendingMeta(meta: MetaMap): Promise<void> {
  await StorageManager.setLocalValue(await userKey(META_KEY), meta);
}

/** Notes what the retry of a pending card needs (merged into what is already known). */
export async function rememberPendingCard(
  cardId: string,
  info: Partial<PendingMeta> = {}
): Promise<void> {
  const meta = await readPendingMeta();
  const before = meta[cardId];
  meta[cardId] = {
    ...before,
    ...Object.fromEntries(Object.entries(info).filter(([, v]) => v !== undefined)),
    attempts: info.attempts ?? before?.attempts ?? 0,
  };
  await writePendingMeta(meta);
}

export async function forgetPendingCards(cardIds: string[]): Promise<void> {
  if (cardIds.length === 0) return;
  const meta = await readPendingMeta();
  let changed = false;
  for (const id of cardIds) {
    if (id in meta) {
      delete meta[id];
      changed = true;
    }
  }
  if (changed) await writePendingMeta(meta);
}

/**
 * A card for a word whose text is not prepared yet: just the word and its place in "Up next to
 * learn". It is never shown in a review until `content_status` is 'ready'.
 */
export function buildPendingCard(input: {
  lemma: string;
  userId: string;
  language?: string;
  source: Parameters<typeof upNextFields>[0];
  /** When the learner chose the word (keeps its place in the list even if made later). */
  queuedAt?: string;
  now?: Date;
  /** One meaning of a word with several, and its hint (shown while the card is prepared). */
  senseKey?: string;
  senseHint?: string | null;
}): Card {
  const now = input.now ?? new Date();
  const nowIso = now.toISOString();
  const lemma = input.lemma.trim();
  return {
    id: crypto.randomUUID ? crypto.randomUUID() : `card_${Date.now()}_${Math.random()}`,
    user_id: input.userId,
    target_language: input.language || 'de',
    lemma,
    front_text: lemma,
    fsrs_state: 'New',
    stability: 0,
    difficulty: 0,
    reps: 0,
    lapses: 0,
    last_review: null,
    due_date: nowIso,
    ...upNextFields(input.source, input.queuedAt ? new Date(input.queuedAt) : now),
    content_status: 'pending',
    retired_at: null,
    ...(input.senseKey || input.senseHint
      ? { sense_key: input.senseKey ?? '', sense_hint: input.senseHint ?? null }
      : {}),
    created_at: nowIso,
    updated_at: nowIso,
  };
}

/**
 * The deck id to record on a card as "chosen in this deck": null while the deck itself is not
 * uploaded yet (the label points at a deck row, which the server would refuse before it exists).
 */
export async function sourceDeckFor(deckId: string): Promise<string | null> {
  try {
    return (await (await getCloudDecksApi()).isUploaded(deckId)) ? deckId : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------------------------
// "Couldn't add": words that turned out not to be German
// ---------------------------------------------------------------------------------------------

export interface CouldntAdd {
  term: string;
  reason: string;
  at: string;
}

/** Remembers a chosen word that had to be dropped, to tell the learner once. */
export async function recordCouldntAdd(term: string, reason: string): Promise<void> {
  const key = await userKey(COULDNT_ADD_KEY);
  const list = (await StorageManager.getLocalValue<CouldntAdd[]>(key)) ?? [];
  await StorageManager.setLocalValue(key, [
    ...list,
    { term, reason, at: new Date().toISOString() },
  ]);
}

/** The words that could not be added since the last call, once each. */
export async function takeCouldntAdd(): Promise<CouldntAdd[]> {
  const key = await userKey(COULDNT_ADD_KEY);
  const list = (await StorageManager.getLocalValue<CouldntAdd[]>(key)) ?? [];
  if (list.length > 0) await StorageManager.setLocalValue(key, []);
  return list;
}
